import type { AppModule } from "@/lib/modules"

export type AppRole = "morador" | "porteiro" | "sindico" | "administracao" | "superadmin"

export type NavigationGroup = "main" | "commercial" | "platform"

export type NavigationIcon =
  | "home"
  | "messages"
  | "building"
  | "users"
  | "package"
  | "alert"
  | "calendar"
  | "bell"
  | "reports"
  | "money"
  | "areas"
  | "visitor"
  | "access"
  | "staff"
  | "clock"
  | "timesheet"
  | "pets"
  | "settings"
  | "briefcase"
  | "shield"
  | "key"
  | "database"
  | "analytics"

export interface AccessRule {
  roles: readonly AppRole[]
  /** Basta ter um dos módulos listados. */
  module?: AppModule | readonly AppModule[]
}

export interface NavigationDefinition extends AccessRule {
  title: string
  url: string
  icon: NavigationIcon
  group: NavigationGroup
}

const condominiumRoles = ["morador", "porteiro", "sindico", "administracao"] as const
const managementRoles = ["sindico", "administracao"] as const
const accessRoles = ["porteiro", "sindico", "administracao"] as const

export const routeAccess = {
  dashboard: { roles: [...condominiumRoles, "superadmin"] },
  messages: { roles: condominiumRoles, module: "full_itacondo" },
  units: { roles: managementRoles, module: "full_itacondo" },
  residents: { roles: managementRoles, module: "full_itacondo" },
  packages: { roles: condominiumRoles, module: "full_itacondo" },
  incidents: { roles: condominiumRoles, module: "full_itacondo" },
  agenda: { roles: condominiumRoles, module: "full_itacondo" },
  notices: { roles: condominiumRoles, module: "full_itacondo" },
  reports: { roles: ["sindico", "administracao"] },
  finance: { roles: managementRoles, module: "full_itacondo" },
  commonAreas: { roles: ["morador", "sindico", "administracao"], module: "full_itacondo" },
  visitors: { roles: accessRoles, module: "access_control" },
  access: { roles: accessRoles, module: "access_control" },
  destinations: { roles: managementRoles, module: "access_control" },
  staff: { roles: managementRoles, module: ["access_control", "time_clock"] },
  timeClock: { roles: ["porteiro", "sindico", "administracao"], module: "time_clock" },
  timesheets: { roles: managementRoles, module: "time_clock" },
  pets: { roles: ["morador"], module: "full_itacondo" },
  meetings: { roles: managementRoles, module: "full_itacondo" },
  settings: { roles: [...condominiumRoles, "superadmin"] },
  superadmin: { roles: ["superadmin"] },
} satisfies Record<string, AccessRule>

export const navigationDefinitions: readonly NavigationDefinition[] = [
  { title: "Dashboard", url: "/dashboard", icon: "home", group: "main", ...routeAccess.dashboard },
  { title: "Mensagens", url: "/mensagens", icon: "messages", group: "main", ...routeAccess.messages },
  { title: "Unidades", url: "/unidades", icon: "building", group: "main", ...routeAccess.units },
  { title: "Moradores", url: "/moradores", icon: "users", group: "main", ...routeAccess.residents },
  { title: "Encomendas", url: "/encomendas", icon: "package", group: "main", ...routeAccess.packages },
  { title: "Ocorrências", url: "/ocorrencias", icon: "alert", group: "main", ...routeAccess.incidents },
  { title: "Agenda", url: "/agenda", icon: "calendar", group: "main", ...routeAccess.agenda },
  { title: "Avisos", url: "/avisos", icon: "bell", group: "main", ...routeAccess.notices },
  { title: "Financeiro", url: "/financeiro", icon: "money", group: "main", ...routeAccess.finance },
  { title: "Áreas Comuns", url: "/areas-comuns", icon: "areas", group: "main", ...routeAccess.commonAreas },
  { title: "Visitantes", url: "/visitantes", icon: "visitor", group: "main", ...routeAccess.visitors },
  { title: "Controle de Acesso", url: "/acessos", icon: "access", group: "main", ...routeAccess.access },
  { title: "Destinos de Acesso", url: "/destinos", icon: "building", group: "main", ...routeAccess.destinations },
  { title: "Colaboradores", url: "/colaboradores", icon: "staff", group: "main", ...routeAccess.staff },
  { title: "Ponto Digital", url: "/ponto-digital", icon: "clock", group: "main", ...routeAccess.timeClock },
  { title: "Folhas de Ponto", url: "/folhas-ponto", icon: "timesheet", group: "main", ...routeAccess.timesheets },
  { title: "Relatórios", url: "/relatorios", icon: "reports", group: "main", ...routeAccess.reports },
  { title: "Pets", url: "/pets", icon: "pets", group: "main", ...routeAccess.pets },
  { title: "Configurações", url: "/configuracoes", icon: "settings", group: "main", ...routeAccess.settings },
  { title: "Gestão Comercial", url: "/gestao-comercial", icon: "briefcase", group: "commercial", ...routeAccess.superadmin },
  { title: "Visão Geral", url: "/superadmin", icon: "shield", group: "platform", ...routeAccess.superadmin },
  { title: "Organizações", url: "/superadmin/organizacoes", icon: "building", group: "platform", ...routeAccess.superadmin },
  { title: "Usuários", url: "/superadmin/usuarios", icon: "users", group: "platform", ...routeAccess.superadmin },
  { title: "Chaves de Licença", url: "/superadmin/licencas", icon: "key", group: "platform", ...routeAccess.superadmin },
  { title: "Gestão de Dados", url: "/superadmin/dados", icon: "database", group: "platform", ...routeAccess.superadmin },
  { title: "Métricas e Analytics", url: "/superadmin/analytics", icon: "analytics", group: "platform", ...routeAccess.superadmin },
]

interface AuthorizationContext {
  role?: string | null
  hasModule: (module: AppModule) => boolean
}

export const canAccess = ({ role, hasModule }: AuthorizationContext, rule: AccessRule) => {
  if (!role || !rule.roles.includes(role as AppRole)) return false
  if (!rule.module) return true
  const modules: readonly AppModule[] = typeof rule.module === "string" ? [rule.module] : rule.module
  return modules.some(hasModule)
}
